import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  RadioGroup,
  Radio,
  FormControlLabel,
  MenuItem,
  Button,
  CircularProgress,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ShieldCheck, ShieldOff, ShieldAlert } from "lucide-react";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ModernSelect from "../ModernSelect";
import UnsavedChangesHint from "../UnsavedChangesHint";
import { tokens } from "../Triage/styles";
import { useReportDirty } from "../../hooks/useLeaveGuard";

const themeTokens = (theme) => tokens(theme.palette.type === "dark");

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    background: themeTokens(theme).surface,
    border: `1px solid ${themeTokens(theme).border}`,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: 0,
  },
  badge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  badgeRequired: {
    background: theme.palette.type === "dark"
      ? "rgba(16, 185, 129, 0.18)"
      : "rgba(16, 185, 129, 0.12)",
    color: theme.palette.type === "dark" ? "#34d399" : "#059669",
    border: "1px solid rgba(16, 185, 129, 0.3)",
  },
  badgeOptional: {
    background: theme.palette.type === "dark"
      ? "rgba(91, 124, 255, 0.18)"
      : "rgba(91, 124, 255, 0.12)",
    color: theme.palette.type === "dark" ? "#7c9aff" : "#3D5CFD",
    border: "1px solid rgba(91, 124, 255, 0.3)",
  },
  badgeOff: {
    background: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.04)"
      : "rgba(0, 0, 0, 0.04)",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.45)" : "#94a3b8",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(0,0,0,0.06)",
  },
  textWrap: { display: "flex", flexDirection: "column", minWidth: 0 },
  title: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#f1f5f9" : "#1e293b",
  },
  status: { fontSize: "0.8rem", fontWeight: 500, marginTop: 2 },
  statusRequired: { color: theme.palette.type === "dark" ? "#34d399" : "#059669" },
  statusOptional: { color: theme.palette.type === "dark" ? "#7c9aff" : "#3D5CFD" },
  statusOff: { color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "#94a3b8" },
  controls: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  graceWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
  },
  graceSelect: { minWidth: 160 },
  options: {
    "& .MuiFormControlLabel-label": { fontSize: 14 },
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  graceLabel: {
    fontSize: "0.8rem",
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#cbd5e1" : "#475569",
  },
  primaryBtn: {
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
}));

const GRACE_OPTIONS = [3, 7, 14, 30];

// onSaved(mfaPolicy): avisa o pai da política gravada (sem isto, sair da seção
// e voltar mostrava a política antiga). onDirtyChange(bool), opcional: avisa
// quando a escolha na tela difere da política salva.
const MfaPolicyManager = ({ companyId, initialPolicy, onSaved, onDirtyChange }) => {
  const classes = useStyles();
  // Política em vigor: vem do pai e é atualizada aqui mesmo depois de salvar
  // (funciona também sem onSaved).
  const [saved, setSaved] = useState({
    mode: initialPolicy?.mode || "off",
    grace: initialPolicy?.gracePeriodDays || 7,
  });
  const savedMode = saved.mode;
  const savedGrace = saved.grace;
  const [mode, setMode] = useState(savedMode);
  const [grace, setGrace] = useState(savedGrace);
  const [saving, setSaving] = useState(false);
  // O prazo só conta quando a autenticação é obrigatória (só aí ele aparece).
  const dirty = mode !== savedMode || (mode === "required" && grace !== savedGrace);
  useReportDirty(dirty, onDirtyChange);

  useEffect(() => {
    if (initialPolicy) {
      const next = { mode: initialPolicy.mode || "off", grace: initialPolicy.gracePeriodDays || 7 };
      setSaved(next);
      setMode(next.mode);
      setGrace(next.grace);
    }
  }, [initialPolicy]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/companies/${companyId}/mfa-policy`, {
        mode,
        gracePeriodDays: grace,
      });
      toast.success(i18n.t("mfa.policy.saved"));
      const policy = data?.mfaPolicy || { mode, gracePeriodDays: grace };
      setSaved({ mode: policy.mode || "off", grace: policy.gracePeriodDays || 7 });
      if (onSaved) onSaved(policy);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // O selo e o status mostram a política em vigor (a salva), não a escolha em edição.
  const badgeClass =
    savedMode === "required" ? classes.badgeRequired :
    savedMode === "optional" ? classes.badgeOptional :
    classes.badgeOff;
  const statusClass =
    savedMode === "required" ? classes.statusRequired :
    savedMode === "optional" ? classes.statusOptional :
    classes.statusOff;
  const BadgeIcon =
    savedMode === "required" ? ShieldAlert :
    savedMode === "optional" ? ShieldCheck :
    ShieldOff;
  const statusLabel =
    savedMode === "required" ? i18n.t("mfa.policy.modeRequired") :
    savedMode === "optional" ? i18n.t("mfa.policy.modeOptional") :
    i18n.t("mfa.policy.modeOff");

  return (
    <Paper className={classes.card} elevation={0}>
      <div className={classes.row}>
        <div className={classes.left}>
          <div className={`${classes.badge} ${badgeClass}`}>
            <BadgeIcon size={22} strokeWidth={1.75} />
          </div>
          <div className={classes.textWrap}>
            <span className={classes.title}>{i18n.t("mfa.policy.title")}</span>
            <span className={`${classes.status} ${statusClass}`}>
              {statusLabel}
            </span>
          </div>
        </div>
      </div>

      <Box className={classes.controls}>
        <RadioGroup
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          className={classes.options}
          aria-label={i18n.t("mfa.policy.title")}
        >
          <FormControlLabel value="off" control={<Radio color="primary" />} label={i18n.t("mfa.policy.modeOff")} />
          <FormControlLabel value="optional" control={<Radio color="primary" />} label={i18n.t("mfa.policy.modeOptional")} />
          <FormControlLabel value="required" control={<Radio color="primary" />} label={i18n.t("mfa.policy.modeRequired")} />
        </RadioGroup>

        {mode === "required" && (
          <div className={classes.graceWrap}>
            <span className={classes.graceLabel}>{i18n.t("mfa.policy.gracePeriod")}</span>
            <ModernSelect
              size="small"
              value={grace}
              onChange={(e) => setGrace(Number(e.target.value))}
              className={classes.graceSelect}
              MenuProps={{ disableScrollLock: true }}
              SelectDisplayProps={{ "aria-label": i18n.t("mfa.policy.gracePeriod") }}
            >
              {GRACE_OPTIONS.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </ModernSelect>
          </div>
        )}
      </Box>

      <div className={classes.actions}>
        <UnsavedChangesHint show={dirty} />
        <Button
          variant="contained"
          color="primary"
          className={classes.primaryBtn}
          onClick={handleSave}
          disabled={saving || !dirty}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {i18n.t("mfa.policy.save")}
        </Button>
      </div>
    </Paper>
  );
};

export default MfaPolicyManager;
